import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Link,
  ProductImage,
  ProductName,
  ProductPrice,
  SwatchTile,
  SwatchTileGroup,
} from 'anf-core-react';

import AddToBagButton from '../../components/AddToBagButton/AddToBagButton';
import BreakpointContext from '../../contexts/BreakpointContext';
import AnalyticsTriggers from '../../helpers/analyticsTriggers';

export default function FullStickyAddToBag({
  brand,
  productName,
  productImage,
  price,
  swatchImage,
  productId,
  sizeLabel,
  addToBagLabel,
  activeButtonLabel,
  handleAddToBag,
  sizeTileSelectionRef,
  swatchSelectionRef,
  addToBagError,
  isFullBleedVisible,
}) {
  const [shouldScroll, setShouldScroll] = useState(false);
  const breakpoints = useContext(BreakpointContext);

  const getNavbarOffset = useCallback(() => (breakpoints.large ? 75 : 55), [breakpoints.large]);

  const handleScrollToElementRef = useCallback((elementRef) => {
    if (elementRef && elementRef.current) {
      const topPosition = elementRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: topPosition - getNavbarOffset(),
        behavior: 'smooth',
      });
    }
  }, [getNavbarOffset]);

  const handleAtbClick = () => {
    window.digitalData.set('addToBag.stickyAtb', true);
    setShouldScroll(true);
    handleAddToBag();
  };

  useEffect(() => {
    if (addToBagError?.value && shouldScroll) {
      setShouldScroll(false);
      handleScrollToElementRef(sizeTileSelectionRef);
      setTimeout(() => {
        AnalyticsTriggers.universalClick('jump link fail', 'add to bag');
      }, 0);
    }
  }, [addToBagError, handleScrollToElementRef, shouldScroll, sizeTileSelectionRef]);

  const handleSizeSelectionScroll = useCallback(() => {
    handleScrollToElementRef(sizeTileSelectionRef);
    setTimeout(() => {
      AnalyticsTriggers.universalClick('jump link success', 'add to bag');
    }, 0);
  }, [sizeTileSelectionRef, handleScrollToElementRef]);

  return (
    <div className={`full-sticky-add-to-bag-container ${isFullBleedVisible ? 'isVisible' : ''}`}>
      <div data-testid="full-sticky-add-to-bag" className="full-sticky-add-to-bag">
        <div className="full-sticky-add-to-bag__image">
          <ProductImage
            src={productImage}
            alt="Main product images"
          />
        </div>
        <div className="full-sticky-add-to-bag__description-container">
          <div className="full-sticky-add-to-bag__description">
            <ProductName brand={brand}>
              {productName}
            </ProductName>
            <ProductPrice
              brand={brand}
              description={price?.description}
              discountPrice={price?.discountPrice}
              discountText={price?.discountText}
              originalPrice={price?.originalPrice}
              variant="discount"
            />
          </div>
        </div>

        <div className="full-sticky-add-to-bag__actions">
          <button className="full-sticky-add-to-bag__swatch-button" onClick={() => handleScrollToElementRef(swatchSelectionRef)}>
            <SwatchTileGroup legend={<span />}>
              <SwatchTile
                isChecked
                hasUnavailableStyle={false}
                id="sticky-selected-swatch-tile"
                label=""
                onChange={() => {}}
                altText="Currently selected swatch color"
                src={swatchImage}
                name={`swatch-${productId}`}
                type="radio"
                value={productId}
              />
            </SwatchTileGroup>
          </button>

          <div className="full-sticky-add-to-bag__size-container">
            <Link onClick={handleSizeSelectionScroll}>
              {sizeLabel}
            </Link>
          </div>

          <div className="full-sticky-add-to-bag__add-to-bag">
            <AddToBagButton
              brand={brand}
              label={addToBagLabel}
              handleAddToBag={handleAtbClick}
              activeButtonLabel={activeButtonLabel}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

FullStickyAddToBag.propTypes = {
  brand: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  productImage: PropTypes.string.isRequired,
  swatchImage: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  sizeLabel: PropTypes.string.isRequired,
  addToBagLabel: PropTypes.string.isRequired,
  activeButtonLabel: PropTypes.string.isRequired,
  handleAddToBag: PropTypes.func.isRequired,
  addToBagError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.bool,
    PropTypes.number,
  ]).isRequired,
  isFullBleedVisible: PropTypes.bool,
};

FullStickyAddToBag.defaultProps = {
  isFullBleedVisible: false,
};
